import { FC, useMemo } from "react";
import { formatTitle } from "utils/formatTitle";
import { StepDetails } from "types/landlord/verificationTemplate";
import { ApplicantType, VerificationNames } from "shared/types";
import { Checkbox, Hide, Show } from "shared/components";
import { useTemplateContext } from "../provider";
import {
  buildPath,
  extractSchemaProperties,
  getValueByPath,
  mapKeyToTitle
} from "../utils";
import { CheckComponent } from "../components/CheckComponent";
import { ConfigurableCard } from "../components/ConfigurableCard";
import { VerificationSchemaView } from "./types";
import { basicInformationTabs, incomeHistoryTabs } from "../constant";

interface SchemaDetailsProps {
  toggleServiceEnabled?: () => void;
  toggleItemServiceRequired: (keys: string[]) => void;
}

const titlesMas = {
  "verifications.personal-details.steps.personal-details.title":
    "Personal information",
  pets: "Pets and Service Animals",
  "verifications.personal-details.steps.emergency-contacts.title":
    "Emergency contacts",
  military_first_responder_teacher: "Military, first responder, or teacher",
  vehicles: "Vehicle information",
  ["personal details"]: "Personal information"
};

const getSwitchLabel = (
  title: string,
  customerType: "guarantor" | "applicant",
  askCustomer: boolean
): [string, string] => {
  const formattedTitle = formatTitle(title.replace("Add ", ""), titlesMas);

  const asked = askCustomer ? "Ask" : "Don't ask";

  if (title === "military_first_responder_teacher")
    return [
      `Ask ${customerType} if they are in the military, a first responder, or a teacher`,
      `${asked} ${customerType} if they are in the military, a first responder, or a teacher`
    ];

  return [
    `Ask ${customerType} about ${formattedTitle.toLocaleLowerCase()}`,
    `${asked} ${customerType} about ${formattedTitle.toLocaleLowerCase()}`
  ];
};

const SupportingDocuments = {
  key: "supporting_documents",
  title: "Supporting documents",
  type: "boolean"
};

export const SchemaDetails: FC<SchemaDetailsProps> = ({
  toggleServiceEnabled,
  toggleItemServiceRequired
}) => {
  const {
    readOnly,
    partnerType,
    template,
    selectedStep,
    combinedVerificationSchemas,
    selectedVerificationSchema
  } = useTemplateContext();

  const isIncomeHistoryTabs = incomeHistoryTabs.includes(
    selectedVerificationSchema
  );
  const isRentalApplicationTabs = basicInformationTabs.includes(
    selectedVerificationSchema
  );

  const selectedVerification = isRentalApplicationTabs
    ? VerificationNames.RentalApplication
    : isIncomeHistoryTabs
      ? VerificationNames.IncomeVerification
      : selectedVerificationSchema;

  const askCustomer =
    template?.verification_schemas[partnerType][selectedVerification]?.[
      `${combinedVerificationSchemas[selectedStep].name}${isIncomeHistoryTabs ? "" : "_step"}_enabled`
    ];

  const fieldRequired =
    template?.verification_schemas[partnerType][selectedVerification]?.[
      `${combinedVerificationSchemas[selectedStep].name}${isIncomeHistoryTabs ? "" : "_step"}_required`
    ];

  const fieldRequiredPaths = buildPath(fieldRequired ?? {}, [
    "verification_schemas",
    partnerType,
    selectedVerification,
    `${combinedVerificationSchemas[selectedStep].name}${isIncomeHistoryTabs ? "" : "_step"}_required`
  ]);

  const customerType =
    partnerType === ApplicantType.CoSigner ? "guarantor" : "applicant";

  const verificationEnabled =
    template?.verification_schemas[partnerType][selectedVerification]?.[
      selectedVerification + "_step_enabled"
    ] ??
    Boolean(template?.verification_schemas[partnerType][selectedVerification]);

  const askCustomerV = verificationEnabled && (askCustomer ?? true);

  const title = useMemo((): string => {
    const stepSchema = combinedVerificationSchemas[selectedStep];
    if (stepSchema.json_schema.title) return stepSchema.json_schema.title;
    return stepSchema.name || "";
  }, [combinedVerificationSchemas, selectedStep]);

  const formattedTitle = formatTitle(title.replace("Add ", ""), titlesMas);
  const [titleLabel, readOnlyLabel] = getSwitchLabel(
    title,
    customerType,
    askCustomerV
  );

  const baseDetails = extractSchemaProperties(
    selectedStep,
    combinedVerificationSchemas
  );

  const details =
    selectedVerificationSchema === VerificationSchemaView.Pets
      ? baseDetails.concat(SupportingDocuments)
      : baseDetails;

  return (
    <ConfigurableCard
      title={formattedTitle}
      switchProps={
        combinedVerificationSchemas[selectedStep].name === "personal_details" ||
        isIncomeHistoryTabs
          ? undefined
          : {
              checked: Boolean(askCustomer),
              disabled: readOnly,
              label: readOnly ? readOnlyLabel : titleLabel,
              onChange: () => toggleServiceEnabled?.()
            }
      }
    >
      <Hide if={readOnly && !askCustomerV}>
        <p className="mb-4 text-sm font-medium text-content-400 mt-4 border-t border-alpha-40 pt-4">
          This information will be requested from {customerType}:
        </p>

        <div className="space-y-2">
          {details.map((source: StepDetails, index: number) => (
            <div
              key={index}
              className="flex justify-between rounded-lg border border-alpha-80 bg-alpha-20 px-4 py-3"
            >
              <p className="text-sm font-medium text-content-500">
                {mapKeyToTitle(source.title)}
              </p>

              <div>
                {readOnly ? (
                  <CheckComponent
                    checked={
                      getValueByPath(
                        template,
                        fieldRequiredPaths[source.key]
                      ) ?? true
                    }
                    label={
                      (getValueByPath<boolean | null>(
                        template,
                        fieldRequiredPaths[source.key]
                      ) ?? true) ? (
                        "Required"
                      ) : (
                        <p className="text-content-200">Optional</p>
                      )
                    }
                  />
                ) : (
                  <Show
                    if={
                      getValueByPath(
                        template,
                        fieldRequiredPaths[source.key]
                      ) !== undefined
                    }
                  >
                    <Checkbox
                      checked={Boolean(
                        getValueByPath(template, fieldRequiredPaths[source.key])
                      )}
                      label="Required"
                      onChange={() =>
                        toggleItemServiceRequired(
                          fieldRequiredPaths[source.key]
                        )
                      }
                      disabled={!askCustomerV}
                    />
                  </Show>
                )}
              </div>
            </div>
          ))}
        </div>
      </Hide>
    </ConfigurableCard>
  );
};
